type Period = '7d' | '30d' | 'all'

type DateRange = {
  dateFrom?: string
  dateTo?: string
}

type ComparableDateRange = {
  current: DateRange
  previous?: DateRange
}

export function periodRanges(now = new Date()): Record<Period, ComparableDateRange> {
  const dateTo = now.toISOString()
  const from = (days: number) => new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString()
  return {
    '7d': {
      current: { dateFrom: from(7), dateTo },
      previous: { dateFrom: from(14), dateTo: from(7) },
    },
    '30d': {
      current: { dateFrom: from(30), dateTo },
      previous: { dateFrom: from(60), dateTo: from(30) },
    },
    all: { current: {} },
  }
}

export function salesComparison(current: number, previous: number, label: string): string {
  const difference = current - previous
  if (previous === 0) {
    if (current === 0) return `Sem vendas também nos ${label}.`
    return `+${current} ${current === 1 ? 'venda' : 'vendas'}; período anterior sem vendas.`
  }
  const rate = (difference / previous) * 100
  const sign = difference > 0 ? '+' : ''
  return `${sign}${difference} ${Math.abs(difference) === 1 ? 'venda' : 'vendas'} (${formatSignedRate(rate)}) vs. ${label}.`
}

export function formatSignedRate(value: number): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${formatRate(value)}`
}

type PerformanceCurrency = {
  currency: string
  processedAmount: string
}

export function comparedCurrencies(
  current: readonly PerformanceCurrency[],
  previous: readonly PerformanceCurrency[],
) {
  return [
    ...new Set([...current.map((item) => item.currency), ...previous.map((item) => item.currency)]),
  ].map((currency) => ({
    currency,
    currentAmount: current.find((item) => item.currency === currency)?.processedAmount ?? '0.000',
    previousAmount: previous.find((item) => item.currency === currency)?.processedAmount ?? '0.000',
  }))
}

export function volumeComparison(current: string, previous: string, label: string): string {
  const currentValue = Number(current)
  const previousValue = Number(previous)
  if (previousValue === 0) {
    return currentValue === 0
      ? `Sem volume também nos ${label}.`
      : `Novo volume; período anterior sem vendas nesta moeda.`
  }
  return `${formatSignedRate(((currentValue - previousValue) / previousValue) * 100)} em volume vs. ${label}.`
}

function formatRate(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: value > 0 && value < 0.1 ? 2 : 1,
    maximumFractionDigits: 2,
  }).format(value / 100)
}
